import React, { useState, FC, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Coffee, PlayCircle, ChevronDown, PhoneIncoming, PhoneOutgoing, Timer,
  LogOut, ArrowRight, ShieldCheck, Clock, Layers, PhoneCall, RotateCcw,
  Activity, X
} from 'lucide-react';
import axios from 'axios';
import mqtt, { MqttClient } from 'mqtt';
// import { useNavigate } from 'react-router-dom'; 
import { ListWrapper } from '../../modules/apps/ticket/components/ticket-list/List';
import { CampaignDetailInfo } from '../../modules/apps/call/campaign-list/form/CampaignDetailInfo';
import { useQuery } from 'react-query'
import { useThemeMode } from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'

const AgentDashboard: FC = () => {
  useEffect(() => {
    document.body.setAttribute('data-kt-app-sidebar-minimize', 'on');
    return () => {
      document.body.removeAttribute('data-kt-app-sidebar-minimize');
    };
  }, []);
  // const navigate = useNavigate(); 
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  const endpoint = `${apiUrl}/dashboard/agent-stats`;

  const { mode } = useThemeMode()
  const [triggerPopData, setTriggerPopData] = useState<{ phone: string, timestamp: number } | null>(null);
  // const lastProcessedCallRef = useRef<{phone: string, time: number} | null>(null);
  const lastStatusRef = useRef<string>("");

  // const handlePopClick = (phone: string) => {
  //   setTriggerPopData({ phone, timestamp: Date.now() });
  //   setActiveTicket(null); 
  // };

  // --- 1. STATE INITIALIZATION ---
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isAux, setIsAux] = useState<boolean>(false);
  const [selectedCampaign, setSelectedCampaign] = useState('Select Campaign');

  const isDark = mode === 'dark' || document.documentElement.getAttribute('data-bs-theme') === 'dark'
  const [activeTicket, setActiveTicket] = useState<any>(null);

  const [auxReasons, setAuxReasons] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);

  const [data, setData] = useState<any>({
    stats: { inbound: 0, outbound: 0, serviceLevel: '0%', avgTalkTime: '00:00' },
    queue: [],
    performance: { current: 0, target: 100 },
    abandoned: []
  });

  const [showAuxMenu, setShowAuxMenu] = useState(false);
  const [showCampaignMenu, setShowCampaignMenu] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');

  const esRef = useRef<EventSource | null>(null);
  const clientRef = useRef<MqttClient | null>(null);
  const auxMenuRef = useRef<HTMLDivElement>(null);
  const campaignMenuRef = useRef<HTMLDivElement>(null);

  const clearTrigger = useCallback(() => {
    setTriggerPopData(null);
  }, []);

  const getApiToken = () => {
    try {
      const raw = localStorage.getItem('kt-auth-react-v');
      if (!raw) return { token: '', ext: '' };
      const parsed = JSON.parse(raw);
      return {
        token: parsed.api_token || '',
        ext: parsed.extension || ''
      };
    } catch { return { token: '', ext: '' }; }
  };

  const { token, ext: agentExt } = getApiToken();
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const { isLoading: isValidatingAgent } = useQuery(
    ['verify-agent', agentExt],
    async () => {
      const res = await axios.get(`${apiUrl}/dashboard/verify`, config);
      return res.data;
    },
    {
      enabled: !!token && !!agentExt,
      refetchOnWindowFocus: false,
      staleTime: 0,
      onSuccess: (resData) => {
        if (resData.authenticated && resData.isLoggedIn) {
          setIsLoggedIn(true);
          setIsAux(resData.isAux);
          setSelectedCampaign(resData.campaignName || 'Select Campaign');
          if (resData.loginAt) {
            localStorage.setItem('agent_login_at', resData.loginAt);
          }
        } else {
          setIsLoggedIn(false);
          setIsAux(false);
        }
      },
      onError: () => {
        setIsLoggedIn(false);
        setIsAux(false);
      }
    }
  );

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [resAux, resCamp] = await Promise.all([
          axios.get(`${apiUrl}/dashboard/aux-reasons`, config),
          axios.get(`${apiUrl}/dashboard/campaigns`, config)
        ]);

        setAuxReasons(resAux.data);

        const inboundOption = { id: 0, name: "inbound" };
        const updatedCampaigns = [inboundOption, ...resCamp.data];

        setCampaigns(updatedCampaigns);
      } catch (err) {
        console.error("Failed to fetch metadata:", err);
      }
    };
    fetchMetadata();
  }, []);

  const activeCampaignData = useMemo(() => {
    return campaigns.find(c => c.name === selectedCampaign && c.name !== 'inbound');
  }, [selectedCampaign, campaigns]);
  const { data: fullCampaignDetail, isLoading: isLoadingDetail } = useQuery(
    ['campaign-detail', activeCampaignData?.id],
    async () => {
      const response = await axios.get(`${apiUrl}/call/campaign/${activeCampaignData?.id}`, config);
      return response.data;
    },
    {
      enabled: !!activeCampaignData?.id,
      refetchOnWindowFocus: false,
      staleTime: 300000,
    }
  );

  const selectedCampaignRef = useRef(selectedCampaign);
  useEffect(() => {
    selectedCampaignRef.current = selectedCampaign;
  }, [selectedCampaign]);

  useEffect(() => {
    const MQTT_URL = process.env.REACT_APP_MQTT_URL;
    if (isLoggedIn && agentExt) {
      const client = mqtt.connect(`wss://${MQTT_URL}`, {
        clientId: `AgentDash_${agentExt}_${Math.random().toString(16).slice(2, 8)}`,
        username: 'user_asterisk',
        password: 'password_rahasia',
        clean: true,
        reconnectPeriod: 2000,
      });

      clientRef.current = client;

      client.on('connect', () => {
        client.subscribe(`dashboard/ext_status/${agentExt}`);
      });

      client.on('message', (_topic, payload) => {
        try {
          const rawData = JSON.parse(payload.toString());
          const currentStatus = rawData.status;
          const currentCaller = rawData.caller;
          const prevStatus = lastStatusRef.current;


          if (currentStatus === "UP" && prevStatus !== "UP" && currentCaller) {
            if (selectedCampaignRef.current === 'inbound') {
              if (prevStatus === "RINGING") {
                const customerPhone = (rawData.target && rawData.target !== agentExt) ? rawData.target : currentCaller;
                setTriggerPopData({
                  phone: customerPhone,
                  timestamp: Date.now()
                });
              }
            }
            setActiveTicket(null);
          }

          if (currentStatus === "RINGING" && prevStatus !== "RINGING" && currentCaller) {
            if (selectedCampaignRef.current === 'inbound') {
              const customerPhone = (rawData.target && rawData.target !== agentExt) ? rawData.target : currentCaller;
              setActiveTicket({
                callerNumber: customerPhone,
                display: rawData.display || 'Incoming Call'
              });
            }
          }

          if (currentStatus === "IDLE" && prevStatus !== "IDLE") {
            setActiveTicket(null);
          }

          lastStatusRef.current = currentStatus;

        } catch (err) {
          console.error('MQTT Parsing Error', err);
        }
      });

      return () => { if (client) client.end(true); };
    }
  }, [isLoggedIn, agentExt]);


  const handleLogin = async () => {
    if (selectedCampaign === 'Select Campaign') return alert("Pilih Campaign dulu!");
    try {
      const res = await axios.post(`${apiUrl}/dashboard/login`, { campaignName: selectedCampaign }, config);
      if (res.data.success) {
        setIsLoggedIn(true);
        // setSeconds(0);
        // localStorage.setItem('agent_login_at', new Date().toLocaleString());  
        connect();
        window.location.reload();
      }
    } catch (err) { alert("Login Gagal!"); }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${apiUrl}/dashboard/logout`, {}, config);
    } finally {
      setIsLoggedIn(false);
      setIsAux(false);
      localStorage.removeItem('agent_login_at');
      cleanup();
      window.location.reload();
    }
  };

  const handleAuxAction = async (id: number | null) => {
    const isStarting = id !== null;
    const url = isStarting ? `${apiUrl}/dashboard/aux/start` : `${apiUrl}/dashboard/aux/stop`;
    try {
      await axios.post(url, isStarting ? { auxReasonId: id } : {}, config);
      setIsAux(isStarting);
      localStorage.setItem('agent_is_aux', isStarting.toString());
      setShowAuxMenu(false);
    } catch (err) { console.error("AUX Error", err); }
  };

  // SSE Logic
  const connect = () => {
    cleanup();
    if (!isLoggedIn) return;
    const sseUrl = `${endpoint}${endpoint.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
    const es = new EventSource(sseUrl);
    esRef.current = es;
    es.onopen = () => setConnectionStatus('connected');
    es.onmessage = (e) => setData(JSON.parse(e.data));
    es.onerror = () => setConnectionStatus('disconnected');
  };

  const cleanup = () => { esRef.current?.close(); setConnectionStatus('disconnected'); };

  useEffect(() => {
    if (isLoggedIn) {
      const syncTime = () => {
        const loginAt = localStorage.getItem('agent_login_at');
        if (loginAt) {
          const startTime = new Date(loginAt).getTime();
          const now = new Date().getTime();
          const diffInSeconds = Math.floor((now - startTime) / 1000);

          setSeconds(diffInSeconds > 0 ? diffInSeconds : 0);
        }
      };

      syncTime();
      connect();
    }
    return () => cleanup();
  }, [isLoggedIn]);

  useEffect(() => {
    let interval: any;
    if (isLoggedIn && !isAux) interval = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isLoggedIn, isAux]);


  const ScreenPop = () => {
    if (!activeTicket) return null;
    return (
      <div className="position-fixed bottom-0 end-0 m-10 animate__animated animate__headShake animate__infinite animate__slow" style={{ zIndex: 9999, width: '350px' }}>
        <div className="card shadow-lg border-0 border-top border-primary border-4 bg-body shadow-lg">
          <div className="card-body p-7">
            <div className="d-flex align-items-center mb-5">
              <div className="bg-light-primary p-3 rounded-circle me-4 animate__animated animate__pulse animate__infinite">
                <PhoneIncoming size={24} className="text-primary" />
              </div>
              <div className="flex-grow-1">
                <h4 className="fw-boldest m-0 text-gray-900 fs-5">Call Incoming</h4>
                <span className="text-muted fs-8 fw-bold">Extension: {agentExt}</span>
              </div>
              <button className="btn btn-icon btn-sm btn-active-light-primary" onClick={() => setActiveTicket(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="mb-6 text-center bg-light-secondary rounded p-4 border border-dashed">
              <span className="text-gray-500 fs-9 fw-boldest text-uppercase d-block mb-1">Customer Number</span>
              <h2 className="fw-boldest text-gray-800 m-0 letter-spacing-2">{activeTicket.callerNumber}</h2>
            </div>

          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-vh-100 w-100 bg-body">
      <ScreenPop />
      {/* --- NAVBAR --- */}
      <nav className="navbar shadow-sm py-3 px-8 bg-body border-bottom border-secondary border-opacity-10">
        <div className="container-fluid p-0">
          <div className="d-flex align-items-center gap-3">
            <div className={`badge badge-circle w-10px h-10px ${connectionStatus === 'connected' ? 'bg-success' : 'bg-danger'}`}></div>
            <div className="position-relative" ref={campaignMenuRef}>
              <button
                className={`btn d-flex align-items-center px-4 py-2 rounded-3 border-0 fs-7 fw-boldest shadow-sm ${isLoggedIn ? 'bg-secondary bg-opacity-10 text-muted' : 'bg-body text-primary border'}`}
                onClick={() => !isLoggedIn && setShowCampaignMenu(!showCampaignMenu)}
              >
                <Layers size={18} className="me-2 text-primary" />
                <span>{selectedCampaign}</span>
                {!isLoggedIn && <ChevronDown size={14} className="ms-2 opacity-50" />}
              </button>
              {showCampaignMenu && (
                <div className="position-absolute start-0 mt-2 card shadow-lg border-0 py-2 w-250px bg-body" style={{ zIndex: 10000 }}>
                  {campaigns.map(c => (
                    <div key={c.id} className="px-5 py-3 fs-7 fw-bold text-gray-700 hover-bg-light cursor-pointer"
                      onClick={() => { setSelectedCampaign(c.name); setShowCampaignMenu(false); }}>
                      {c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            {isLoggedIn && (
              <div className="bg-gray-900 text-gray-100 px-3 py-2 rounded-3 d-flex align-items-center me-2">
                <Clock className="text-primary me-2" size={14} />
                <span className="fs-7 fw-boldest font-monospace">{new Date(seconds * 1000).toISOString().substr(11, 8)}</span>
              </div>
            )}

            {!isLoggedIn ? (
              <button className="btn btn-primary btn-sm fw-boldest px-4 py-2" onClick={handleLogin}>LOGIN</button>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <div className="position-relative" ref={auxMenuRef}>
                  <button
                    className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-3 fw-boldest border-0 ${isAux ? 'btn-warning text-white' : 'btn-success text-white'}`}
                    onClick={() => isAux ? handleAuxAction(null) : setShowAuxMenu(!showAuxMenu)}
                  >
                    {isAux ? <PlayCircle size={16} className="me-1" /> : <Coffee size={16} className="me-1" />}
                    <span className="d-none d-md-inline">{isAux ? 'On Break' : 'Available'}</span>
                    <ChevronDown size={12} className="ms-2 opacity-50" />
                  </button>
                  {showAuxMenu && (
                    <div className="position-absolute end-0 mt-2 card shadow-lg border-0 py-2 w-180px bg-body" style={{ zIndex: 10000 }}>
                      {auxReasons.map(r => (
                        <button key={r.id} className="btn btn-active-light-primary text-gray-700 text-start fw-bold fs-7 py-3 px-5 border-0 bg-transparent w-100"
                          onClick={() => handleAuxAction(r.id)}>
                          {r.reasonName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button className="btn btn-icon btn-sm btn-light-danger rounded-3" onClick={handleLogout}><LogOut size={18} /></button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="container-fluid p-8">
        <div className="row g-4 mb-8 text-start">
          <div className="col-12 col-xl-9">
            <div className="row g-4 mb-8 text-start">
              {[
                { l: 'Inbound', v: data.stats?.inbound, i: <PhoneIncoming size={20} />, c: 'primary', color: '#005AAA' },
                { l: 'Outbound', v: data.stats?.outbound, i: <PhoneOutgoing size={20} />, c: 'primary', color: '#005AAA' },
                { l: 'S. Level', v: data.stats?.serviceLevel, i: <ShieldCheck size={20} />, c: 'primary', color: '#005AAA' },
                { l: 'Avg Talk', v: data.stats?.avgTalkTime, i: <Timer size={20} />, c: 'primary', color: '#005AAA' }
              ].map((s, idx) => (
                <div className="col-6 col-md-3" key={idx}>
                  <div
                    className="card border-0 shadow-sm h-100 bg-body"
                    style={{
                      background: isDark
                        ? `linear-gradient(135deg, ${s.color}33 0%, ${s.color}15 100%)`
                        : `linear-gradient(135deg, ${s.color} 0%, ${s.color}dd 100%)`,
                      borderRadius: '20px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      border: isDark ? `1px solid ${s.color}44` : 'none',
                      boxShadow: isDark ? `0 10px 20px -10px ${s.color}44` : `0 10px 15px -5px ${s.color}66`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                      e.currentTarget.style.boxShadow = isDark
                        ? `0 20px 30px -10px ${s.color}88`
                        : `0 20px 25px -5px ${s.color}aa`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = isDark
                        ? `0 10px 20px -10px ${s.color}44`
                        : `0 10px 15px -5px ${s.color}66`;
                    }}
                  >
                    <div className="card-body p-5 d-flex align-items-center"
                      style={{
                        backgroundColor: isDark ? `${s.color}44` : 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(4px)'
                      }}
                    >
                      <div className={`bg-light-${s.c} text-${s.c} p-3 rounded-3 me-4`} style={{ color: isDark ? s.color : '#ffffff', opacity: isDark ? 1 : 0.9 }}>{s.i}</div>
                      <div>
                        <span className="fs-9 fw-boldest text-uppercase d-block mb-1" style={{ color: isDark ? s.color : '#ffffff', opacity: isDark ? 1 : 0.9 }}>{s.l}</span>
                        <span className="fs-4 fw-boldest lh-1" style={{ color: isDark ? s.color : '#ffffff', opacity: isDark ? 1 : 0.9 }}>{s.v || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

            </div>
          </div>
          <div className="col-12 col-xl-3">
            <div className="card border-0 shadow-sm rounded-4 bg-body">
              <div className="card-header border-0 pt-6 px-6"><h3 className="card-title fw-boldest text-gray-900 fs-6 m-0">Live Incoming Queue</h3></div>
              <div className="card-body p-6">
                {data.queue?.length > 0 ? data.queue.map((d: any, idx: number) => (
                  <div key={idx} className="d-flex align-items-center p-3 rounded-3 mb-3 bg-light-primary bg-opacity-5 border border-dashed border-primary border-opacity-20">
                    <div className="bg-body p-2 rounded-2 shadow-xs me-3 text-primary"><PhoneCall size={16} /></div>
                    <div className="flex-grow-1 min-w-0">
                      <span className="d-block fw-boldest fs-7 text-gray-900 text-truncate">{d.phone}</span>
                      <span className="fs-9 text-gray-500 fw-bold">Wait: {d.waitTime}</span>
                    </div>
                  </div>
                )) : <div className="text-center py-5 text-gray-400 fs-7 fw-bold">No calls in queue</div>}
              </div>
            </div>
          </div>
        </div>
        {/* QUEUE */}

        <div className="row">
          <div className="col-12" style={{ position: 'relative', zIndex: 2 }}>
            {
              (isLoggedIn) ?
                (!fullCampaignDetail || selectedCampaign === 'inbound') ? (
                  <div className="card shadow-sm border-0">
                    <ListWrapper
                      externalTicketTrigger={triggerPopData}
                      onTriggerProcessed={clearTrigger}
                    />
                  </div>
                ) : (
                  <CampaignDetailInfo
                    data={fullCampaignDetail}
                    externalTicketTrigger={triggerPopData}
                    onTriggerProcessed={clearTrigger}
                  />
                )
                : <></>
            }
          </div>
        </div>


        <div className="row mt-5 g-6 text-start">
          {/* <div className="col-12 col-md-6 col-xl-8">
            <div className="card border-0 shadow-sm rounded-4 h-100 bg-body card-flush pt-3 mb-5">
              <div className="card-header border-0 pt-6 px-6">
                <h3 className="card-title fw-boldest text-gray-900 fs-6 m-0">Pending Callbacks</h3>
              </div>
              <div className="card-body p-6">
                {[].map((name, i) => (
                  <div key={i} className="d-flex align-items-center p-3 rounded-3 mb-3 border border-secondary border-opacity-10 shadow-xs">
                    <div className="flex-grow-1 min-w-0">
                      <span className="d-block fw-boldest fs-7 text-gray-900 text-truncate">{name}</span>
                      <span className="fs-9 text-gray-500 fw-bold">10:{20+i} AM</span>
                    </div>
                    <button className="btn btn-sm btn-light-primary fw-boldest rounded-2 px-3 py-1">DIAL</button>
                  </div>
                ))}
              </div>
            </div>
          </div> */}

          {/* PERFORMANCE */}
          {/* <div className="col-12 col-md-6 col-xl-12">
            
            <div className="card border-0 shadow-sm rounded-4 bg-light-danger bg-opacity-10">
               <div className="card-body p-6">
                  <h5 className="fw-boldest text-danger fs-7 mb-3">Abandoned</h5>
                  
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;